import { useAuth } from '@aimsos/auth';
import { useWorkspaceSelectorData } from './workspace-selector/hooks/useWorkspaceSelectorData.js';
import SelectorTopBar from './workspace-selector/components/SelectorTopBar.jsx';
import SelectorHeader from './workspace-selector/components/SelectorHeader.jsx';
import SelectorFilters from './workspace-selector/components/SelectorFilters.jsx';
import SelectorSort from './workspace-selector/components/SelectorSort.jsx';
import ResumeBanner from './workspace-selector/components/ResumeBanner.jsx';
import WorkspaceList from './workspace-selector/components/WorkspaceList.jsx';

/**
 * Post-login Workspace Selector — the rich "choose a workspace" landing (see
 * the login-landing prototype). Shown by WorkspaceGate when the signed-in user
 * has access to more than one workspace and hasn't landed on a specific one's
 * subdomain yet.
 *
 * Selecting an entry (or a studio chip) enters that workspace: for a workspace
 * other than the active one that means fetching a JWT scoped to it and handing
 * it off to its subdomain; for the active one it's a direct handoff with the
 * token we already hold. Both live in `useWorkspaceSelectorData`, which also
 * overlays the mock industry / role / studio-access / last-visited metadata
 * until the backend returns it.
 */
const WorkspacePicker = () => {
  const { user } = useAuth();
  const {
    active,
    others,
    metaById,
    accessCount,
    totalCount,
    isLoading,
    isLoadingMore,
    hasMore,
    loadMore,
    openWorkspace,
    switchingId,
    switchError,
    hasQuery,
    lastVisited,
    searchKeyword,
    onSearchChange,
    sortMode,
    onSortChange,
  } = useWorkspaceSelectorData();

  // May legitimately be empty (a handoff origin where Auth0 has no session and
  // the cached profile never arrived) — SelectorHeader then greets without a
  // name rather than falling back to an untranslatable placeholder.
  const displayName = user?.given_name || user?.name?.split(' ')[0] || '';

  return (
    <div className="relative min-h-screen bg-surface-base text-content">
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-brand-500/[0.06] via-transparent to-transparent" />

      <div className="relative z-10 flex min-h-screen flex-col">
        <SelectorTopBar user={user} />

        <main className="mx-auto w-full max-w-4xl flex-1 px-6 pb-16 pt-4 lg:px-10">
          <SelectorHeader name={displayName} count={accessCount} />

          <SelectorFilters
            query={searchKeyword}
            onQueryChange={onSearchChange}
            sortControl={<SelectorSort mode={sortMode} onCycle={onSortChange} />}
          />

          {/* Hidden while searching: the banner is about the session, not the
              current result set, and it would sit above unrelated matches. */}
          {!hasQuery && (
            <ResumeBanner
              record={lastVisited}
              busy={switchingId != null && switchingId === lastVisited?.workspaceId}
              disabled={switchingId != null}
              onResume={openWorkspace}
            />
          )}

          {switchError && (
            <div
              role="alert"
              className="mb-4 rounded-xl border border-[#D32F2F] bg-[#FDEDED] px-4 py-3 text-sm text-[#5F2120] dark:border-red-500 dark:bg-red-950/40 dark:text-red-100"
            >
              {switchError}
            </div>
          )}

          <WorkspaceList
            active={active}
            others={others}
            metaById={metaById}
            loading={isLoading}
            isLoadingMore={isLoadingMore}
            hasMore={hasMore}
            loadMore={loadMore}
            totalCount={totalCount}
            hasQuery={hasQuery}
            switchingId={switchingId}
            onSelect={openWorkspace}
            onOpenStudio={openWorkspace}
          />
        </main>
      </div>
    </div>
  );
};

export default WorkspacePicker;
